/**
 * HR 휴가 승인 관리 페이지
 *
 * V50: 2단계 승인 플로우 (orgAndHr)
 * - 조직장 승인 완료(orgApproved) 상태의 휴가 요청을 HR이 최종 승인
 * - status === 'orgApproved' && type in ['leave', 'leaveCancel']
 *
 * V73: 2탭 구조 (승인요청 / 히스토리)
 * - 승인요청탭: 기존 HR 승인 대기 목록
 * - 히스토리탭: HR 처리 완료(승인/반려) 이력
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { useApprovalMutations } from '../../shared/hooks/useApprovalMutations';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { Pagination, paginateData } from '../../shared/components/Pagination';
import { ApprovalRequest, ApprovalRequestStatus } from '../../shared/types/approvalRequest';
import {
  X,
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  CheckSquare,
  Square,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { formatDateTimeByCompanyCode } from '../../shared/utils/dateUtils';  // V62

// ============================================
// 타입/라벨 매핑
// ============================================

const APPROVAL_TYPE_LABELS: Record<string, string> = {
  leave: '휴가',
  leaveCancel: '휴가취소',
  leaveOfAbsence: '휴직',  // V66: 휴직 통합
  leaveOfAbsenceUpdate: '휴직 수정 및 연장',  // V66
};

/**
 * V73: 휴가 요청 유형 칼럼에 실제 휴가 종류 표시
 * V66: 휴직 분기 추가 — requestData.typeName(예: 육아휴직) 우선
 */
function getTypeDisplayLabel(type: string, requestData?: any): string {
  if (type === 'leave') {
    return requestData?.leaveType || APPROVAL_TYPE_LABELS.leave;
  }
  if (type === 'leaveCancel') {
    const leaveName = requestData?.leaveTypeName || requestData?.leaveType || '휴가';
    return `${leaveName} 취소`;
  }
  if (type === 'leaveOfAbsence') {
    const loaName = requestData?.typeName || requestData?.type || '휴직';
    return loaName === '휴직' ? '휴직' : `${loaName} (휴직)`;
  }
  if (type === 'leaveOfAbsenceUpdate') {
    const loaName = requestData?.updated?.typeName || requestData?.typeName || '휴직';
    return `${loaName} 수정`;
  }
  return APPROVAL_TYPE_LABELS[type] || type;
}

const STATUS_LABELS: Record<ApprovalRequestStatus, string> = {
  pending: '승인대기',
  orgApproved: '인사팀 승인대기',
  approved: '승인',
  approved_scheduled: '예약대기',
  applied: '적용완료',
  rejected: '반려',
  canceled: '취소',
  revoked: '취소됨',
};

const STATUS_STYLES: Record<ApprovalRequestStatus, { bg: string; color: string }> = {
  pending: { bg: '#FEF3C7', color: '#D97706' },
  orgApproved: { bg: '#DBEAFE', color: '#2563EB' },
  approved: { bg: '#DCFCE7', color: '#16A34A' },
  approved_scheduled: { bg: '#FFF7ED', color: '#EA580C' },
  applied: { bg: '#DCFCE7', color: '#16A34A' },
  rejected: { bg: '#FEE2E2', color: '#DC2626' },
  canceled: { bg: '#F3F4F6', color: '#6B7280' },
  revoked: { bg: '#F3E8FF', color: '#7C3AED' },
};

// V55.7: 취소 승인 요청 상태 라벨
const CANCEL_STATUS_LABELS: Record<string, string> = {
  pending: '취소승인대기',
  orgApproved: '인사팀 취소승인대기',
  cancelled: '취소완료',
  rejected: '취소반려',
};

// V55.7: 취소 승인 요청 상태 스타일
const CANCEL_STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  pending: { bg: '#FEF3C7', color: '#D97706' },      // 노란색 (대기)
  orgApproved: { bg: '#DBEAFE', color: '#2563EB' },  // 파란색 (HR 대기)
  cancelled: { bg: '#F3F4F6', color: '#6B7280' },    // 회색 (완료)
  rejected: { bg: '#FEE2E2', color: '#DC2626' },     // 빨간색 (반려)
};

/**
 * V55.7: 상태 라벨 및 스타일 결정 (cancelStatus 우선)
 */
function getStatusDisplay(request: ApprovalRequest): { label: string; bg: string; color: string } {
  // cancelRequested=true이고 cancelStatus가 있으면 취소 상태 표시
  if ((request as any).cancelRequested === true || (request as any).cancelStatus) {
    const cancelStatus = (request as any).cancelStatus || 'pending';
    return {
      label: CANCEL_STATUS_LABELS[cancelStatus] || '취소승인대기',
      ...(CANCEL_STATUS_STYLES[cancelStatus] || CANCEL_STATUS_STYLES.pending),
    };
  }
  // 일반 상태 표시
  return {
    label: STATUS_LABELS[request.status] || request.status,
    ...(STATUS_STYLES[request.status] || STATUS_STYLES.pending),
  };
}

// ============================================
// 유틸리티 함수
// ============================================

/**
 * V62: Timestamp를 회사 타임존 기준 문자열로 변환
 *
 * 정책 (V62-3): 이 timestamp는 "행위자(HR) 개인 시간"이 아니라
 * "해당 요청이 속한 회사(request.companyCode)의 시간"으로 표시한다.
 * 예: KR HR이 CN 휴가요청을 승인해도 CN 시간으로 표시.
 * 이유: 동일 요청 내 createdAt·orgApprovedAt·hrApprovedAt 등의 시간 기준 통일.
 */
function formatTimestamp(timestamp: any, companyCode?: string | null): string {
  return formatDateTimeByCompanyCode(timestamp, companyCode);
}

function extractLeaveInfo(requestData: any, type?: string): string {
  if (!requestData) return '-';

  // V66: 휴직(leaveOfAbsence) — typeName + startDate ~ expectedReturnDate + reason
  if (type === 'leaveOfAbsence') {
    const loaParts: string[] = [];
    if (requestData.typeName || requestData.type) loaParts.push(requestData.typeName || requestData.type);
    if (requestData.startDate) {
      const end = requestData.expectedReturnDate || requestData.endDate;
      loaParts.push(end ? `${requestData.startDate} ~ ${end}` : requestData.startDate);
    }
    if (requestData.reason) loaParts.push(`사유: ${requestData.reason}`);
    return loaParts.join(' / ') || '-';
  }

  // V66: 휴직 수정 및 연장 — original / updated 비교
  if (type === 'leaveOfAbsenceUpdate') {
    const orig = requestData.original || {};
    const upd = requestData.updated || {};
    const o = `${orig.startDate || '?'} ~ ${orig.expectedReturnDate || '미정'}`;
    const u = `${upd.startDate || '?'} ~ ${upd.expectedReturnDate || '미정'}`;
    return `${upd.typeName || orig.typeName || '휴직'} / ${o} → ${u}`;
  }

  const parts: string[] = [];
  if (requestData.leaveTypeName || requestData.leaveType) {
    parts.push(requestData.leaveTypeName || requestData.leaveType);
  }
  if (requestData.startDate) {
    if (requestData.endDate && requestData.endDate !== requestData.startDate) {
      parts.push(`${requestData.startDate} ~ ${requestData.endDate}`);
    } else {
      parts.push(requestData.startDate);
    }
  }
  if (requestData.totalDays) {
    parts.push(`${requestData.totalDays}일`);
  }

  return parts.join(' / ') || '-';
}

// ============================================
// 탭 타입
// ============================================

type TabType = 'pending' | 'history';

// ============================================
// 메인 컴포넌트
// ============================================

export function HrLeaveApprovalsPage() {
  const { user } = useCurrentUser();
  const { hrApproveRequest, getHrPendingList, getHrApprovalHistory, isProcessing } = useApprovalMutations();

  // V61: role_exec 조회 전용 — 승인/반려 액션 비활성화
  const { hasPermission } = useAuthorization();
  const canWrite = hasPermission('hr.manage');

  // V73: 탭 상태
  const [activeTab, setActiveTab] = useState<TabType>('pending');

  // === 승인요청탭 상태 ===
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    success: number;
    failed: number;
    failedDetails: Array<{ id: string; requesterName: string; error: string }>;
  } | null>(null);

  // === 히스토리탭 상태 ===
  const [historyRequests, setHistoryRequests] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyYear, setHistoryYear] = useState(new Date().getFullYear());
  const [historySelectedRequest, setHistorySelectedRequest] = useState<ApprovalRequest | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 15;

  // 연도 옵션
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = 2020; y <= currentYear; y++) {
      years.push(y);
    }
    return years.reverse();
  }, []);

  // === 승인요청탭 로직 ===
  const fetchPendingData = useCallback(async () => {
    if (!user?.profile?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await getHrPendingList();
      setRequests(data || []);
    } catch (err: any) {
      let userMessage = '데이터 조회에 실패했습니다.';
      if (err.code === 'permission-denied') {
        userMessage = '인사팀 권한이 없습니다. 관리자에게 문의하세요.';
      } else if (err.code === 'unauthenticated') {
        userMessage = '로그인이 필요합니다. 다시 로그인해 주세요.';
      } else if (err.message) {
        userMessage = err.message;
      }
      setError(userMessage);
    } finally {
      setIsLoading(false);
    }
  }, [getHrPendingList, user?.profile?.id]);

  // === 히스토리탭 로직 ===
  const fetchHistoryData = useCallback(async () => {
    if (!user?.profile?.id) return;

    try {
      setHistoryLoading(true);
      setHistoryError(null);
      const data = await getHrApprovalHistory(historyYear);
      setHistoryRequests(data || []);
    } catch (err: any) {
      let userMessage = '히스토리 조회에 실패했습니다.';
      if (err.code === 'permission-denied') {
        userMessage = '인사팀 권한이 없습니다.';
      } else if (err.message) {
        userMessage = err.message;
      }
      setHistoryError(userMessage);
    } finally {
      setHistoryLoading(false);
    }
  }, [getHrApprovalHistory, historyYear, user?.profile?.id]);

  // 초기 로딩: 승인요청탭
  useEffect(() => {
    if (user?.profile?.id) {
      fetchPendingData();
    }
  }, [user?.profile?.id]);

  // 히스토리탭 전환 시 로딩
  useEffect(() => {
    if (activeTab === 'history' && user?.profile?.id) {
      fetchHistoryData();
    }
  }, [activeTab, historyYear, user?.profile?.id]);

  // 체크박스 로직
  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    if (requests.length === 0) return;
    const allSelected = requests.every((r) => selectedIds.has(r.id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(requests.map((r) => r.id)));
    }
  }, [requests, selectedIds]);

  const isAllSelected = requests.length > 0 && requests.every((r) => selectedIds.has(r.id));

  // 일괄 승인
  const handleBulkApprove = useCallback(async () => {
    if (selectedIds.size === 0) return;

    setIsBulkApproving(true);
    setBulkResult(null);

    const result = {
      success: 0,
      failed: 0,
      failedDetails: [] as Array<{ id: string; requesterName: string; error: string }>,
    };

    const selectedRequests = requests.filter((r) => selectedIds.has(r.id));

    for (const req of selectedRequests) {
      try {
        await hrApproveRequest({
          approvalRequestId: req.id,
          action: 'approve',
          comment: '',
        });
        result.success++;
      } catch (err: any) {
        result.failed++;
        result.failedDetails.push({
          id: req.id,
          requesterName: req.requesterName || '-',
          error: err.message || '알 수 없는 오류',
        });
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setIsBulkApproving(false);
    setBulkResult(result);
    setSelectedIds(new Set());
    fetchPendingData();
  }, [selectedIds, requests, hrApproveRequest, fetchPendingData]);

  return (
    <div style={{ padding: '24px' }}>
      {/* 페이지 제목 */}
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '8px',
        }}
      >
        승인 관리
      </h1>
      <p style={{ fontSize: '14px', color: 'var(--color-gray-500)', marginBottom: '24px' }}>
        조직장 승인 완료된 휴가/휴직 요청을 최종 승인합니다.
      </p>

      {/* V73: 탭 헤더 */}
      <div
        style={{
          display: 'flex',
          borderBottom: '2px solid var(--color-gray-200)',
          marginBottom: '16px',
        }}
      >
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: activeTab === 'pending' ? '600' : '400',
            color: activeTab === 'pending' ? '#2563EB' : 'var(--color-gray-500)',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'pending' ? '2px solid #2563EB' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer',
          }}
        >
          승인요청 {requests.length > 0 && `(${requests.length})`}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: activeTab === 'history' ? '600' : '400',
            color: activeTab === 'history' ? '#2563EB' : 'var(--color-gray-500)',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'history' ? '2px solid #2563EB' : '2px solid transparent',
            marginBottom: '-2px',
            cursor: 'pointer',
          }}
        >
          히스토리
        </button>
      </div>

      {/* 일괄 승인 결과 토스트 */}
      {bulkResult && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 20px',
            background: bulkResult.failed > 0 ? '#FEF3C7' : '#DCFCE7',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1100,
            maxWidth: '400px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>일괄 승인 완료</div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
                성공: {bulkResult.success}건
                {bulkResult.failed > 0 && ` / 실패: ${bulkResult.failed}건`}
              </div>
              {bulkResult.failedDetails.length > 0 && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#DC2626' }}>
                  {bulkResult.failedDetails.map((detail) => (
                    <div key={detail.id}>• {detail.requesterName}: {detail.error}</div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setBulkResult(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* 승인요청탭 */}
      {/* ============================================ */}
      {activeTab === 'pending' && (
        <>
          {/* 필터/액션 영역 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <button
              onClick={handleBulkApprove}
              disabled={!canWrite || selectedIds.size === 0 || isBulkApproving}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                background: selectedIds.size === 0 ? 'var(--color-gray-200)' : '#16A34A',
                color: selectedIds.size === 0 ? 'var(--color-gray-500)' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: selectedIds.size === 0 || isBulkApproving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {isBulkApproving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  처리 중...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  일괄 승인 {selectedIds.size > 0 && `(${selectedIds.size})`}
                </>
              )}
            </button>

            <button
              onClick={fetchPendingData}
              disabled={isLoading}
              style={{
                padding: '8px',
                background: 'white',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="새로고침"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div
              style={{
                padding: '12px 16px',
                background: '#FEE2E2',
                color: '#DC2626',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {/* 테이블 */}
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '40px 140px 100px 100px minmax(170px, 1fr) 130px 130px 70px',
                padding: '12px 16px',
                background: 'var(--color-gray-50)',
                borderBottom: '1px solid var(--color-gray-200)',
                fontWeight: '600',
                fontSize: '13px',
                color: 'var(--color-gray-600)',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={handleToggleSelectAll}
                  disabled={requests.length === 0}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: requests.length === 0 ? 'not-allowed' : 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isAllSelected ? '#16A34A' : 'var(--color-gray-400)',
                  }}
                >
                  {isAllSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                </button>
              </div>
              <div>유형</div>
              <div>신청자</div>
              <div>소속</div>
              <div>요청 정보</div>
              <div>신청일시</div>
              <div>조직장 승인</div>
              <div style={{ textAlign: 'center' }}>작업</div>
            </div>

            {isLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                로딩 중...
              </div>
            ) : requests.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                인사팀 승인 대기 중인 요청이 없습니다.
              </div>
            ) : (
              requests.map((request, index) => (
                <div
                  key={request.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 140px 100px 100px minmax(170px, 1fr) 130px 130px 70px',
                    padding: '12px 16px',
                    borderBottom: index === requests.length - 1 ? 'none' : '1px solid var(--color-gray-100)',
                    fontSize: '13px',
                    color: 'var(--color-gray-700)',
                    alignItems: 'center',
                    background: selectedIds.has(request.id) ? 'rgba(22, 163, 74, 0.05)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleToggleSelect(request.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: selectedIds.has(request.id) ? '#16A34A' : 'var(--color-gray-400)',
                      }}
                    >
                      {selectedIds.has(request.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                    </button>
                  </div>

                  <div style={{ fontWeight: '500' }}>
                    {getTypeDisplayLabel(request.type, request.requestData)}
                    {(request as any).cancelRequested === true && (
                      <span style={{ color: '#DC2626', marginLeft: '4px' }}>(취소)</span>
                    )}
                  </div>

                  <div>{request.requesterName || '-'}</div>

                  <div style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                    {request.requesterOrgUnitName || '-'}
                  </div>

                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {extractLeaveInfo(request.requestData, request.type)}
                  </div>

                  <div style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                    {formatTimestamp(request.createdAt, request.companyCode)}
                  </div>

                  <div style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                    {formatTimestamp(request.orgApprovedAt, request.companyCode)}
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => setSelectedRequest(request)}
                      style={{
                        padding: '4px 8px',
                        background: 'var(--color-gray-100)',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        color: 'var(--color-gray-600)',
                      }}
                    >
                      <Eye size={14} />
                      상세
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div
            style={{
              textAlign: 'center',
              marginTop: '12px',
              fontSize: '13px',
              color: 'var(--color-gray-500)',
            }}
          >
            총 {requests.length}건
          </div>

          {/* 상세/승인/반려 모달 */}
          {selectedRequest && (
            <HrApprovalDetailModal
              request={selectedRequest}
              canWrite={canWrite}
              onClose={() => setSelectedRequest(null)}
              onActionComplete={() => {
                setSelectedRequest(null);
                fetchPendingData();
              }}
            />
          )}
        </>
      )}

      {/* ============================================ */}
      {/* V73: 히스토리탭 */}
      {/* ============================================ */}
      {activeTab === 'history' && (
        <>
          {/* 필터 영역 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>연도:</label>
              <select
                value={historyYear}
                onChange={(e) => { setHistoryYear(Number(e.target.value)); setHistoryPage(1); }}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  background: 'white',
                }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}년
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={fetchHistoryData}
              disabled={historyLoading}
              style={{
                padding: '8px',
                background: 'white',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '6px',
                cursor: historyLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="새로고침"
            >
              <RefreshCw size={16} className={historyLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* 에러 메시지 */}
          {historyError && (
            <div
              style={{
                padding: '12px 16px',
                background: '#FEE2E2',
                color: '#DC2626',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <AlertCircle size={18} />
              {historyError}
            </div>
          )}

          {/* 히스토리 테이블 */}
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '140px 100px 100px minmax(150px, 1fr) 100px 130px 130px 70px',
                padding: '12px 16px',
                background: 'var(--color-gray-50)',
                borderBottom: '1px solid var(--color-gray-200)',
                fontWeight: '600',
                fontSize: '13px',
                color: 'var(--color-gray-600)',
                alignItems: 'center',
              }}
            >
              <div>유형</div>
              <div>신청자</div>
              <div>소속</div>
              <div>요청 정보</div>
              <div style={{ textAlign: 'center' }}>처리결과</div>
              <div>처리일시</div>
              <div>처리자</div>
              <div style={{ textAlign: 'center' }}>상세</div>
            </div>

            {historyLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                로딩 중...
              </div>
            ) : historyRequests.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                {historyYear}년 처리된 휴가 승인 이력이 없습니다.
              </div>
            ) : (
              paginateData(historyRequests, historyPage, HISTORY_PAGE_SIZE).map((request, index, pageData) => {
                const isApproved = request.status === 'approved';
                return (
                  <div
                    key={request.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '140px 100px 100px minmax(150px, 1fr) 100px 130px 130px 70px',
                      padding: '12px 16px',
                      borderBottom: index === pageData.length - 1 ? 'none' : '1px solid var(--color-gray-100)',
                      fontSize: '13px',
                      color: 'var(--color-gray-700)',
                      alignItems: 'center',
                    }}
                  >
                    {/* 유형 */}
                    <div style={{ fontWeight: '500' }}>
                      {getTypeDisplayLabel(request.type, request.requestData)}
                    </div>

                    {/* 신청자 */}
                    <div>{request.requesterName || '-'}</div>

                    {/* 소속 */}
                    <div style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                      {request.requesterOrgUnitName || '-'}
                    </div>

                    {/* 휴가 정보 */}
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {extractLeaveInfo(request.requestData, request.type)}
                    </div>

                    {/* 처리결과 */}
                    <div style={{ textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: isApproved ? '#DCFCE7' : '#FEE2E2',
                          color: isApproved ? '#16A34A' : '#DC2626',
                        }}
                      >
                        {isApproved ? '승인' : '반려'}
                      </span>
                    </div>

                    {/* 처리일시 */}
                    <div style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                      {formatTimestamp(request.hrApprovedAt || request.hrRejectedAt || request.approvedAt || request.rejectedAt, request.companyCode)}
                    </div>

                    {/* 처리자 */}
                    <div style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                      {request.hrApproverName || '-'}
                    </div>

                    {/* 상세 */}
                    <div style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => setHistorySelectedRequest(request)}
                        style={{
                          padding: '4px 8px',
                          background: 'var(--color-gray-100)',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          color: 'var(--color-gray-600)',
                        }}
                      >
                        <Eye size={14} />
                        상세
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div
            style={{
              textAlign: 'center',
              marginTop: '12px',
              fontSize: '13px',
              color: 'var(--color-gray-500)',
            }}
          >
            총 {historyRequests.length}건
          </div>

          <Pagination
            currentPage={historyPage}
            totalItems={historyRequests.length}
            pageSize={HISTORY_PAGE_SIZE}
            onPageChange={setHistoryPage}
          />

          {/* 히스토리 상세 모달 (읽기전용) */}
          {historySelectedRequest && (
            <HrHistoryDetailModal
              request={historySelectedRequest}
              onClose={() => setHistorySelectedRequest(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ============================================
// 상세/승인/반려 모달 (승인요청탭)
// ============================================

interface HrApprovalDetailModalProps {
  request: ApprovalRequest;
  onClose: () => void;
  onActionComplete: () => void;
  canWrite?: boolean; // V61: role_exec 조회 전용
}

function HrApprovalDetailModal({ request, onClose, onActionComplete, canWrite = true }: HrApprovalDetailModalProps) {
  const { hrApproveRequest, isProcessing } = useApprovalMutations();
  const [comment, setComment] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const handleApprove = async () => {
    try {
      setActionError(null);
      await hrApproveRequest({
        approvalRequestId: request.id,
        action: 'approve',
        comment,
      });
      onActionComplete();
    } catch (err: any) {
      setActionError(err.message || '인사팀 승인 처리 중 오류가 발생했습니다.');
    }
  };

  // V282: 취소 요청 여부 판정
  const isCancelRequest = !!(request as any).cancelRequested;

  const handleReject = async () => {
    // V282: 취소 요청에 대해서는 인사팀 반려 불가
    if (isCancelRequest) {
      setActionError('취소 요청은 반려할 수 없습니다.');
      return;
    }

    if (!comment.trim()) {
      setActionError('반려 사유를 입력해주세요.');
      return;
    }

    try {
      setActionError(null);
      await hrApproveRequest({
        approvalRequestId: request.id,
        action: 'reject',
        comment,
      });
      onActionComplete();
    } catch (err: any) {
      setActionError(err.message || '인사팀 반려 처리 중 오류가 발생했습니다.');
    }
  };

  const statusDisplay = getStatusDisplay(request);

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
              {getTypeDisplayLabel(request.type, request.requestData)}
              {(request as any).cancelRequested === true && (
                <span style={{ color: '#DC2626', marginLeft: '8px' }}>(취소)</span>
              )}
            </h2>
            <span
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                background: statusDisplay.bg,
                color: statusDisplay.color,
              }}
            >
              {statusDisplay.label}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
              요청자 정보
            </h3>
            <div style={{ background: 'var(--color-gray-50)', padding: '12px 16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{request.requesterName}</div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                {request.requesterOrgUnitName}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
              요청 정보
            </h3>
            <div style={{ background: 'var(--color-gray-50)', padding: '16px', borderRadius: '8px' }}>
              <LeaveRequestDataDisplay
                data={request.requestData}
                type={request.type}
                /* V70.3: 취소 사유는 approvalRequest 최상위 필드 — 조직장 화면과 동일하게 전달 */
                cancelReason={(request as any).cancelRequested ? (request as any).cancelReason : undefined}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
              처리 정보
            </h3>
            <div style={{ background: 'var(--color-gray-50)', padding: '12px 16px', borderRadius: '8px', fontSize: '13px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>요청일시</span>
                <span>{formatTimestamp(request.createdAt, request.companyCode)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>조직장</span>
                <span>{request.approverName || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>조직장 승인일시</span>
                <span>{formatTimestamp(request.orgApprovedAt, request.companyCode)}</span>
              </div>
            </div>
          </div>

          {/* HR 승인/반려 액션 */}
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
              인사팀 최종 승인/반려
            </h3>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="코멘트 (반려 시 필수)"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                marginBottom: '12px',
                resize: 'vertical',
                minHeight: '80px',
              }}
            />

            {actionError && (
              <div
                style={{
                  padding: '10px 12px',
                  background: '#FEE2E2',
                  color: '#DC2626',
                  borderRadius: '6px',
                  fontSize: '13px',
                  marginBottom: '12px',
                }}
              >
                {actionError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleApprove}
                disabled={!canWrite || isProcessing}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#16A34A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <CheckCircle size={18} />
                최종 승인
              </button>
              {/* V282: 취소 요청에 대해서는 반려 버튼 숨김 */}
              {!isCancelRequest && (
              <button
                onClick={handleReject}
                disabled={!canWrite || isProcessing}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                <XCircle size={18} />
                반려
              </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// V73: 히스토리 상세 모달 (읽기전용)
// ============================================

interface HrHistoryDetailModalProps {
  request: any;
  onClose: () => void;
}

function HrHistoryDetailModal({ request, onClose }: HrHistoryDetailModalProps) {
  const isApproved = request.status === 'approved';

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
              {getTypeDisplayLabel(request.type, request.requestData)}
            </h2>
            <span
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                background: isApproved ? '#DCFCE7' : '#FEE2E2',
                color: isApproved ? '#16A34A' : '#DC2626',
              }}
            >
              {isApproved ? '승인' : '반려'}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <div style={{ padding: '24px' }}>
          {/* 요청자 정보 */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
              요청자 정보
            </h3>
            <div style={{ background: 'var(--color-gray-50)', padding: '12px 16px', borderRadius: '8px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{request.requesterName}</div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                {request.requesterOrgUnitName || '-'}
              </div>
            </div>
          </div>

          {/* 휴가 정보 */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
              요청 정보
            </h3>
            <div style={{ background: 'var(--color-gray-50)', padding: '16px', borderRadius: '8px' }}>
              <LeaveRequestDataDisplay
                data={request.requestData}
                type={request.type}
                /* V70.3: 취소 사유는 approvalRequest 최상위 필드 — 조직장 화면과 동일하게 전달 */
                cancelReason={(request as any).cancelRequested ? (request as any).cancelReason : undefined}
              />
            </div>
          </div>

          {/* 처리 정보 */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-600)', marginBottom: '8px' }}>
              처리 정보
            </h3>
            <div style={{ background: 'var(--color-gray-50)', padding: '12px 16px', borderRadius: '8px', fontSize: '13px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>요청일시</span>
                <span>{formatTimestamp(request.createdAt, request.companyCode)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>조직장 승인일시</span>
                <span>{formatTimestamp(request.orgApprovedAt, request.companyCode)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>처리결과</span>
                <span
                  style={{
                    fontWeight: '600',
                    color: isApproved ? '#16A34A' : '#DC2626',
                  }}
                >
                  {isApproved ? '승인' : '반려'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>처리자</span>
                <span>{request.hrApproverName || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-gray-500)' }}>처리일시</span>
                <span>{formatTimestamp(request.hrApprovedAt || request.hrRejectedAt, request.companyCode)}</span>
              </div>
              {request.comment && (
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--color-gray-200)' }}>
                  <div style={{ color: 'var(--color-gray-500)', marginBottom: '4px' }}>코멘트</div>
                  <div style={{ color: 'var(--color-gray-700)' }}>{request.comment}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 휴가 요청 데이터 표시
// ============================================

/**
 * V70.3: `cancelReason` prop 추가
 *
 * 취소 사유는 `approvalRequests` **문서 최상위**의 `cancelReason` 에 저장된다
 * (`approval/functions.ts:925` requestCancelApproval). `requestData` 안에는 들어가지 않는다.
 * 조직장 화면(WorkApprovalsPage.tsx:1314)은 이 값을 별도 prop 으로 넘겨 표시하고 있었으나,
 * HR 화면은 `requestData` 만 넘겨서 취소 사유가 표시되지 않았다.
 * 조직장 화면(RequestDataDisplay)의 표시 규칙을 그대로 재사용한다.
 */
function LeaveRequestDataDisplay({ data, type, cancelReason }: { data: any; type: string; cancelReason?: string }) {
  if (!data) return <div style={{ color: 'var(--color-gray-400)' }}>-</div>;

  const renderRow = (label: string, value: any) => (
    <div style={{ display: 'flex', marginBottom: '8px' }}>
      <div style={{ width: '100px', color: 'var(--color-gray-500)', fontSize: '13px' }}>{label}</div>
      <div style={{ flex: 1, fontSize: '14px' }}>{value || '-'}</div>
    </div>
  );

  // V70.3: 취소 요청 문맥 → 메인 사유란 = 취소사유, 신청사유는 보조 행
  //   (조직장 화면 WorkApprovalsPage.tsx:1572 renderReasonRows 와 동일 규칙)
  const renderReasonRows = () => cancelReason
    ? <>{renderRow('취소사유', cancelReason)}{data.reason && renderRow('신청사유', data.reason)}</>
    : <>{renderRow('사유', data.reason)}</>;

  if (type === 'leaveCancel') {
    return (
      <div>
        {renderRow('휴가유형', data.leaveTypeName || data.leaveType)}
        {renderRow('시작일', data.startDate)}
        {data.endDate && data.endDate !== data.startDate && renderRow('종료일', data.endDate)}
        {renderRow('일수', data.totalDays ? `${data.totalDays}일` : '-')}
        {/* V70.3: 최상위 cancelReason 우선 (requestData 에는 저장되지 않음) */}
        {renderRow('취소사유', cancelReason || data.cancelReason || data.reason)}
      </div>
    );
  }

  // V66: 휴직 수정 및 연장 상세 — original / updated 두 그룹 표시
  if (type === 'leaveOfAbsenceUpdate') {
    const orig = data.original || {};
    const upd = data.updated || {};
    return (
      <div>
        <div style={{ marginBottom: '8px', fontWeight: 600, color: 'var(--color-gray-700)' }}>변경 전</div>
        {renderRow('휴직유형', orig.typeName || orig.type)}
        {renderRow('시작일', orig.startDate)}
        {renderRow('종료일', orig.expectedReturnDate || '미정')}
        {renderRow('사유', orig.reason || '-')}
        <div style={{ marginTop: '12px', marginBottom: '8px', fontWeight: 600, color: 'var(--color-gray-700)' }}>변경 후</div>
        {renderRow('휴직유형', upd.typeName || upd.type)}
        {renderRow('시작일', upd.startDate)}
        {renderRow('종료일', upd.expectedReturnDate || '미정')}
        {renderRow('사유', upd.reason || '-')}
        {data.changeReason && (
          <>
            <div style={{ marginTop: '12px', marginBottom: '8px', fontWeight: 600, color: 'var(--color-gray-700)' }}>변경 사유</div>
            {renderRow('', data.changeReason)}
          </>
        )}
      </div>
    );
  }

  // V66: 휴직(leaveOfAbsence) 상세 — leaveOfAbsenceRequests 의 requestData 구조 그대로
  if (type === 'leaveOfAbsence') {
    return (
      <div>
        {renderRow('휴직유형', data.typeName || data.type)}
        {renderRow('시작일', data.startDate)}
        {renderRow('종료일', data.expectedReturnDate)}
        {/* V70.3: 휴직 취소 요청도 동일 경로(cancelRequested)로 HR 대기열에 오므로 동일 규칙 적용 */}
        {renderReasonRows()}
        {Array.isArray(data.attachments) && data.attachments.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ color: 'var(--color-gray-500)', fontSize: '13px', marginBottom: '6px' }}>첨부파일</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {data.attachments.map((url: string, i: number) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--color-blue-600)' }}>
                  첨부파일 {i + 1}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {renderRow('휴가유형', data.leaveTypeName || data.leaveType)}
      {renderRow('시작일', data.startDate)}
      {data.endDate && data.endDate !== data.startDate && renderRow('종료일', data.endDate)}
      {renderRow('일수', data.totalDays ? `${data.totalDays}일` : '-')}
      {/* V70.3: 취소 요청이면 '취소사유' + '신청사유', 일반 요청이면 기존과 동일하게 '사유' */}
      {renderReasonRows()}
      {Array.isArray(data.attachments) && data.attachments.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <div style={{ color: 'var(--color-gray-500)', fontSize: '13px', marginBottom: '6px' }}>첨부파일</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {data.attachments.map((url: string, i: number) => {
              const isImage = /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url);
              return (
                <div key={i}>
                  {isImage && (
                    <img
                      src={url}
                      alt={`첨부 ${i + 1}`}
                      style={{ maxWidth: '240px', maxHeight: '180px', borderRadius: '6px', marginBottom: '4px', cursor: 'pointer' }}
                      onClick={() => window.open(url, '_blank')}
                    />
                  )}
                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--color-blue-600)' }}>
                    {isImage ? `이미지 ${i + 1}` : `첨부파일 ${i + 1}`}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default HrLeaveApprovalsPage;
